// packages/frontend/core/src/blocksuite/ai/hooks/useChatPanelConfig.ts
import type { TagMeta } from '@affine/core/components/page-list';
import { AIModelService } from '@affine/core/modules/ai-button/services/models';
import { AINetworkSearchService } from '@affine/core/modules/ai-button/services/network-search';
import { AIPlaygroundService } from '@affine/core/modules/ai-button/services/playground';
import { AIReasoningService } from '@affine/core/modules/ai-button/services/reasoning';
import {
  type CollectionMeta,
  CollectionService,
} from '@affine/core/modules/collection';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { DocsSearchService } from '@affine/core/modules/docs-search';
import {
  type SearchCollectionMenuAction,
  type SearchDocMenuAction,
  SearchMenuService,
  type SearchTagMenuAction,
} from '@affine/core/modules/search-menu/services';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { TagService } from '@affine/core/modules/tag';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { createSignalFromObservable } from '@blocksuite/affine/shared/utils';
import { signal } from '@preact/signals-core';
import { useFramework } from '@toeverything/infra';
import { useEffect } from 'react';

const createStaticSignal = <T>(value: T) => ({
  signal: signal(value),
  cleanup: () => {},
});

export function useAIChatConfig() {
  const framework = useFramework();

  const searchService = framework.get(AINetworkSearchService);
  const aiModelService = framework.get(AIModelService);
  const reasoningService = framework.get(AIReasoningService);
  const playgroundService = framework.get(AIPlaygroundService);
  const docDisplayMetaService = framework.get(DocDisplayMetaService);
  const workspaceService = framework.get(WorkspaceService);
  const searchMenuService = framework.get(SearchMenuService);
  const docsSearchService = framework.get(DocsSearchService);
  const tagService = framework.get(TagService);
  const collectionService = framework.get(CollectionService);
  const docsService = framework.get(DocsService);
  const tagsEnabled = useTagsFeatureEnabled();
  const collectionsEnabled = useCollectionsFeatureEnabled();

  // Trigger model loading for the current workspace (idempotent)
  const workspaceId = workspaceService.workspace.id;
  useEffect(() => {
    aiModelService.loadForWorkspace(workspaceId).catch(console.error);
  }, [aiModelService, workspaceId]);

  // Web Search toggle visible only when a published webSearch model
  // with a configured provider API key exists for this workspace.
  const networkSearchConfig = {
    visible: aiModelService.hasWebSearchCapability,
    enabled: searchService.enabled,
    setEnabled: searchService.setEnabled,
  };

  const reasoningConfig = {
    enabled: reasoningService.enabled,
    setEnabled: reasoningService.setEnabled,
    effort: reasoningService.effort,
    setEffort: reasoningService.setEffort,
  };

  const playgroundConfig = {
    visible: playgroundService.visible,
  };

  const docDisplayConfig = {
    getIcon: (docId: string) => {
      return docDisplayMetaService.icon$(docId, { type: 'lit' }).value;
    },
    getTitle: (docId: string) => {
      return docDisplayMetaService.title$(docId).value;
    },
    getTitleSignal: (docId: string) => {
      const titleLive$ = docDisplayMetaService.title$(docId);
      return createSignalFromObservable(titleLive$, '');
    },
    getDocMeta: (docId: string) => {
      const docRecord = docsService.list.doc$(docId).value;
      return docRecord?.meta$.value ?? null;
    },
    getDocPrimaryMode: (docId: string) => {
      const docRecord = docsService.list.doc$(docId).value;
      return docRecord?.primaryMode$.value ?? 'page';
    },
    getDoc: (docId: string) => {
      const doc = workspaceService.workspace.docCollection.getDoc(docId);
      return doc?.getStore() ?? null;
    },
    getReferenceDocs: (docIds: string[]) => {
      const docsLive$ = docsSearchService.watchRefsFrom(docIds);
      return createSignalFromObservable(docsLive$, []);
    },
    getTags: () => {
      if (!tagsEnabled) {
        return createStaticSignal<TagMeta[]>([]);
      }
      const tagMetasLive$ = tagService.tagList.tagMetas$;
      return createSignalFromObservable(tagMetasLive$, []);
    },
    getTagTitle: (tagId: string) => {
      if (!tagsEnabled) {
        return '';
      }
      const tagLive$ = tagService.tagList.tagByTagId$(tagId);
      return tagLive$.value?.value$.value ?? '';
    },
    getTagPageIds: (tagId: string) => {
      if (!tagsEnabled) {
        return [];
      }
      const tagLive$ = tagService.tagList.tagByTagId$(tagId);
      if (!tagLive$) return [];
      return tagLive$.value?.pageIds$.value ?? [];
    },
    getCollections: () => {
      if (!collectionsEnabled) {
        return createStaticSignal<CollectionMeta[]>([]);
      }
      const collectionMetasLive$ = collectionService.collectionMetas$;
      return createSignalFromObservable(collectionMetasLive$, []);
    },
    getCollectionPageIds: (collectionId: string) => {
      if (!collectionsEnabled) {
        return [];
      }
      const collectionLive$ = collectionService.collection$(collectionId);
      // TODO: lack of documents that meet the collection rules
      return collectionLive$?.value?.info$.value.allowList ?? [];
    },
  };

  const searchMenuConfig = {
    tagsEnabled,
    collectionsEnabled,
    getDocMenuGroup: (
      query: string,
      action: SearchDocMenuAction,
      abortSignal: AbortSignal
    ) => {
      return searchMenuService.getDocMenuGroup(query, action, abortSignal);
    },
    getTagMenuGroup: (
      query: string,
      action: SearchTagMenuAction,
      abortSignal: AbortSignal
    ) => {
      return searchMenuService.getTagMenuGroup(query, action, abortSignal);
    },
    getCollectionMenuGroup: (
      query: string,
      action: SearchCollectionMenuAction,
      abortSignal: AbortSignal
    ) => {
      return searchMenuService.getCollectionMenuGroup(
        query,
        action,
        abortSignal
      );
    },
  };

  return {
    networkSearchConfig,
    reasoningConfig,
    docDisplayConfig,
    searchMenuConfig,
    playgroundConfig,
  };
}
